'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckboxItem as DropdownMenuPrimitiveCheckboxItem, Content as DropdownMenuPrimitiveContent, Group as DropdownMenuPrimitiveGroup, Item as DropdownMenuPrimitiveItem, ItemIndicator as DropdownMenuPrimitiveItemIndicator, Label as DropdownMenuPrimitiveLabel, Portal as DropdownMenuPrimitivePortal, RadioGroup as DropdownMenuPrimitiveRadioGroup, RadioItem as DropdownMenuPrimitiveRadioItem, Root as DropdownMenuPrimitiveRoot, Separator as DropdownMenuPrimitiveSeparator, Sub as DropdownMenuPrimitiveSub, SubContent as DropdownMenuPrimitiveSubContent, SubTrigger as DropdownMenuPrimitiveSubTrigger, Trigger as DropdownMenuPrimitiveTrigger } from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
const DropdownMenu = DropdownMenuPrimitiveRoot;
const DropdownMenuTrigger = DropdownMenuPrimitiveTrigger;
const DropdownMenuGroup = DropdownMenuPrimitiveGroup;
const DropdownMenuPortal = DropdownMenuPrimitivePortal;
const DropdownMenuSub = DropdownMenuPrimitiveSub;
const DropdownMenuRadioGroup = DropdownMenuPrimitiveRadioGroup;
const DropdownMenuSubTrigger = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveSubTrigger>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveSubTrigger> & {
  inset?: boolean;
}>((t0, reference) => {
  const $ = _c(14);
  let children;
  let className;
  let inset;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = inset;
    $[4] = properties;
  } else {
    children = $[1];
    className = $[2];
    inset = $[3];
    properties = $[4];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronRight className="ml-auto" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== properties || $[11] !== reference || $[12] !== t2) {
    t4 = <DropdownMenuPrimitiveSubTrigger ref={reference} className={t2} {...properties}>{children}{t3}</DropdownMenuPrimitiveSubTrigger>;
    $[9] = children;
    $[10] = properties;
    $[11] = reference;
    $[12] = t2;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitiveSubTrigger.displayName;
const DropdownMenuSubContent = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveSubContent>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveSubContent>>((t0, reference) => {
  const $ = _c(9);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("z-layer-flyout min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== properties || $[6] !== reference || $[7] !== t1) {
    t2 = <DropdownMenuPrimitiveSubContent ref={reference} className={t1} {...properties} />;
    $[5] = properties;
    $[6] = reference;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitiveSubContent.displayName;
const DropdownMenuContent = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveContent>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveContent>>((t0, reference) => {
  const $ = _c(11);
  let className;
  let properties;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
    $[3] = t1;
  } else {
    className = $[1];
    properties = $[2];
    t1 = $[3];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("z-layer-flyout max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== properties || $[7] !== reference || $[8] !== sideOffset || $[9] !== t2) {
    t3 = <DropdownMenuPrimitivePortal><DropdownMenuPrimitiveContent ref={reference} sideOffset={sideOffset} className={t2} {...properties} /></DropdownMenuPrimitivePortal>;
    $[6] = properties;
    $[7] = reference;
    $[8] = sideOffset;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
DropdownMenuContent.displayName = DropdownMenuPrimitiveContent.displayName;
const DropdownMenuItem = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveItem>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveItem> & {
  inset?: boolean;
}>((t0, reference) => {
  const $ = _c(11);
  let className;
  let inset;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = properties;
  } else {
    className = $[1];
    inset = $[2];
    properties = $[3];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== properties || $[8] !== reference || $[9] !== t2) {
    t3 = <DropdownMenuPrimitiveItem ref={reference} className={t2} {...properties} />;
    $[7] = properties;
    $[8] = reference;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
DropdownMenuItem.displayName = DropdownMenuPrimitiveItem.displayName;
const DropdownMenuCheckboxItem = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveCheckboxItem>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveCheckboxItem>>((t0, reference) => {
  const $ = _c(16);
  let checked;
  let children;
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = properties;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    properties = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== checked) {
    t2 = checked === undefined ? {} : {
      checked
    };
    $[7] = checked;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitiveItemIndicator><Check className="h-4 w-4" /></DropdownMenuPrimitiveItemIndicator></span>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== properties || $[12] !== reference || $[13] !== t1 || $[14] !== t2) {
    t4 = <DropdownMenuPrimitiveCheckboxItem ref={reference} className={t1} {...t2} {...properties}>{t3}{children}</DropdownMenuPrimitiveCheckboxItem>;
    $[10] = children;
    $[11] = properties;
    $[12] = reference;
    $[13] = t1;
    $[14] = t2;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
});
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitiveCheckboxItem.displayName;
const DropdownMenuRadioItem = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveRadioItem>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveRadioItem>>((t0, reference) => {
  const $ = _c(12);
  let children;
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><DropdownMenuPrimitiveItemIndicator><Circle className="h-2 w-2 fill-current" /></DropdownMenuPrimitiveItemIndicator></span>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== properties || $[9] !== reference || $[10] !== t1) {
    t3 = <DropdownMenuPrimitiveRadioItem ref={reference} className={t1} {...properties}>{t2}{children}</DropdownMenuPrimitiveRadioItem>;
    $[7] = children;
    $[8] = properties;
    $[9] = reference;
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
DropdownMenuRadioItem.displayName = DropdownMenuPrimitiveRadioItem.displayName;
const DropdownMenuLabel = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveLabel>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveLabel> & {
  inset?: boolean;
}>((t0, reference) => {
  const $ = _c(11);
  let className;
  let inset;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = properties;
  } else {
    className = $[1];
    inset = $[2];
    properties = $[3];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("px-2 py-1.5 text-sm font-semibold", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== properties || $[8] !== reference || $[9] !== t2) {
    t3 = <DropdownMenuPrimitiveLabel ref={reference} className={t2} {...properties} />;
    $[7] = properties;
    $[8] = reference;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
DropdownMenuLabel.displayName = DropdownMenuPrimitiveLabel.displayName;
const DropdownMenuSeparator = forwardRef<ComponentRef<typeof DropdownMenuPrimitiveSeparator>, ComponentPropsWithoutRef<typeof DropdownMenuPrimitiveSeparator>>((t0, reference) => {
  const $ = _c(9);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== properties || $[6] !== reference || $[7] !== t1) {
    t2 = <DropdownMenuPrimitiveSeparator ref={reference} className={t1} {...properties} />;
    $[5] = properties;
    $[6] = reference;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DropdownMenuSeparator.displayName = DropdownMenuPrimitiveSeparator.displayName;
const DropdownMenuShortcut = t0 => {
  const $ = _c(8);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("ml-auto text-xs tracking-widest opacity-60", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== properties || $[6] !== t1) {
    t2 = <span className={t1} {...properties} />;
    $[5] = properties;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';
export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuRadioItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuRadioGroup };